/**
 * Feature module definitions — single source of truth for which routes,
 * nav keys, and dashboard widgets belong to each toggleable module.
 *
 * Two-level control:
 * 1. Group toggles: enable/disable entire module groups
 * 2. Item toggles: hide individual nav items within enabled groups
 *
 * When `enabled_modules` is null/undefined in settings, ALL modules are enabled.
 * `disabled_items` is an array of nav keys to hide (empty = all visible).
 */

export const TOGGLEABLE_MODULE_KEYS = ['productions', 'scanning', 'tasks', 'financial', 'documents', 'network', 'crew', 'messaging', 'kiosk', 'sustainability', 'touring', 'cables']

export const MODULES = {
  inventory: {
    label: 'moduleInventory',
    description: 'moduleInventoryDesc',
    icon: 'Package',
    alwaysEnabled: true,
    // Brighella v2 (ADR-013 Merge E) — navLoans replaced by navMovements
    // (single hub for internal / outgoing / incoming loans).
    navKeys: ['navItems', 'navWishlist', 'navBundles', 'navLocations', 'navTemplates', 'navMovements', 'navMaintenance', 'navNfcTags', 'navNetworkInventory', 'printCenter'],
    topNavKeys: [],
    routes: ['/items', '/wishlist', '/bundles', '/locations', '/templates', '/loans', '/movements', '/maintenance', '/items/import', '/admin/stale', '/admin/low-stock', '/admin/lost-damaged', '/borrowed', '/nfc-tags', '/sites', '/network-inventory', '/items/print-center'],
    adminNavKeys: ['navImport', 'navStaleItems', 'navLowStock', 'navLostDamaged'],
    widgets: ['by-department', 'storage-locations', 'low-availability', 'expiring-items'],
    statCards: ['active-items', 'replacement-value'],
  },
  productions: {
    label: 'moduleProductions',
    description: 'moduleProductionsDesc',
    requiredPlan: 'Starter+',
    icon: 'Theater',
    navKeys: ['navShows', 'navEvents', 'ridersNav', 'artistsNav', 'incomingTitle', 'navSpaces', 'navBudgets', 'navDepartmentOrders', 'navCalendarSchedule', 'externalProductions', 'navVenueInquiries'],
    topNavKeys: [],
    routes: ['/productions', '/productions/:id', '/events', '/events/:id', '/riders', '/riders/:id', '/artists', '/incoming-productions', '/spaces', '/spaces/:id', '/budgets', '/productions/:id/budget', '/productions/:id/pull-sheet', '/productions/:id/call-sheet', '/department-orders', '/calendar', '/daily', '/season', '/schedule', '/external-productions', '/calendar/overview', '/admin/venue-inquiries'],
    adminNavKeys: [],
    widgets: ['active-productions', 'upcoming-productions', 'on-loan'],
    statCards: ['active-productions', 'on-loan'],
  },
  financial: {
    label: 'moduleFinancial',
    description: 'moduleFinancialDesc',
    requiredPlan: 'Pro+',
    icon: 'DollarSign',
    navKeys: ['navReports', 'navOverview', 'navExplorer', 'navInsurance', 'navForecast', 'navUtilization', 'navUtilizationReport', 'navCostComparison', 'navDepreciation', 'navCashFlow', 'navSeasonComparison', 'navPettyCash', 'navBudgetRevisions'],
    topNavKeys: [],
    routes: ['/financial', '/reports/utilization', '/reports/cost-comparison', '/reports/depreciation', '/financial/cash-flow', '/financial/season-comparison', '/financial/petty-cash', '/financial/budget-revisions'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  scanning: {
    label: 'moduleScanning',
    description: 'moduleScanningDesc',
    requiredPlan: 'Starter+',
    icon: 'ScanLine',
    navKeys: [],
    topNavKeys: ['scan'],
    routes: ['/scan'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  tasks: {
    label: 'moduleTasks',
    description: 'moduleTasksDesc',
    requiredPlan: 'Starter+',
    icon: 'SquareCheckBig',
    navKeys: [],
    topNavKeys: ['tasks'],
    routes: ['/todos'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  documents: {
    label: 'moduleDocuments',
    description: 'moduleDocumentsDesc',
    requiredPlan: 'Starter+',
    icon: 'BookOpen',
    navKeys: ['handbookTitle'],
    topNavKeys: ['documents'],
    routes: ['/documents', '/handbook'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  network: {
    label: 'moduleNetwork',
    description: 'moduleNetworkDesc',
    requiredPlan: 'Pro+',
    icon: 'Globe',
    navKeys: ['navNeedsBoard', 'navBorrowed', 'navOutgoingLoans', 'navNetworkDashboard', 'navMarketplace'],
    topNavKeys: [],
    routes: ['/network', '/marketplace', '/network/requests', '/network/loans', '/tenant/site-templates', '/tenant/vendor-credentials'],
    adminNavKeys: ['navSiteTemplates', 'navVendorCredentials'],
    widgets: [],
    statCards: [],
  },
  crew: {
    label: 'moduleCrew',
    description: 'moduleCrewDesc',
    requiredPlan: 'Starter+',
    icon: 'Users',
    // Brighella v2 (ADR-013 Merge C) — People Hub absorbs crew workload,
    // pto inbox, freelancer timesheets, and the directory. navAvailability +
    // navPeopleDirectory are the new tabs; legacy keys stay listed so they
    // continue to gate on the module toggle for backwards compatibility.
    navKeys: [
      'navCrewWorkload', 'navFreelancerTimesheets',
      'navPeople', 'navPeopleDirectory', 'navAvailability',
      'navSeasonOverview',
    ],
    topNavKeys: ['navMyHours'],
    routes: ['/crew', '/my-hours', '/people', '/season-overview', '/crew/schedule', '/admin/freelancer-timesheets', '/admin/pto'],
    adminNavKeys: ['navPtoInbox'],
    widgets: [],
    statCards: [],
  },
  messaging: {
    label: 'moduleMessaging',
    description: 'moduleMessagingDesc',
    requiredPlan: 'Starter+',
    icon: 'MessageSquare',
    navKeys: ['navComms'],
    topNavKeys: [],
    routes: ['/comms'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  kiosk: {
    label: 'moduleKiosk',
    description: 'moduleKioskDesc',
    requiredPlan: 'Enterprise',
    icon: 'Monitor',
    navKeys: ['kioskMode'],
    topNavKeys: [],
    routes: ['/kiosk', '/kiosk/sort', '/kiosk/inventory', '/kiosk/display'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  sustainability: {
    label: 'moduleSustainability',
    description: 'moduleSustainabilityDesc',
    requiredPlan: 'Pro+',
    icon: 'Leaf',
    navKeys: ['navGreenBook'],
    topNavKeys: [],
    routes: ['/green-book'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  touring: {
    label: 'moduleTouringLabel',
    description: 'moduleTouringDesc',
    requiredPlan: 'Pro+',
    icon: 'MapPin',
    navKeys: ['toursNav'],
    topNavKeys: [],
    routes: ['/tours', '/tours/:id'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
  cables: {
    label: 'cableModuleLabel',
    description: 'cableModuleDesc',
    requiredPlan: 'Starter+',
    icon: 'Cable',
    navKeys: ['navCables'],
    topNavKeys: [],
    routes: ['/cables', '/cables/:id'],
    adminNavKeys: [],
    widgets: [],
    statCards: [],
  },
}

export const ALL_MODULE_KEYS = Object.keys(MODULES)

/**
 * Build a mapping of nav key → module group for item-level lookups.
 */
export const NAV_KEY_TO_MODULE = (() => {
  const map = {}
  for (const [moduleKey, mod] of Object.entries(MODULES)) {
    for (const k of [...mod.navKeys, ...mod.topNavKeys, ...mod.adminNavKeys]) {
      map[k] = moduleKey
    }
  }
  return map
})()

/**
 * Get all nav keys that belong to a module group (for the admin UI).
 */
export function getModuleNavKeys(moduleKey) {
  const mod = MODULES[moduleKey]
  if (!mod) return []
  return [...mod.navKeys, ...mod.topNavKeys, ...mod.adminNavKeys]
}

/**
 * Metadata used by upgrade gates. Kept on MODULES so route, nav, and
 * paywall copy stay bound to the same product contract.
 */
export function getModuleGateMeta(moduleKey) {
  const mod = MODULES[moduleKey]
  if (!mod) {
    return {
      key: moduleKey,
      label: moduleKey || 'upgradeFallbackFeature',
      description: null,
      requiredPlan: 'Pro+',
    }
  }

  return {
    key: moduleKey,
    label: mod.label,
    description: mod.description,
    requiredPlan: mod.requiredPlan || 'Starter+',
  }
}

/**
 * Check if a nav item key is enabled — checks both group toggle AND disabled_items.
 */
export function isNavKeyEnabled(navKey, enabledModules, disabledItems) {
  if (!enabledModules) {
    // null = all modules enabled, but still check disabled_items
    if (Array.isArray(disabledItems) && disabledItems.includes(navKey)) return false
    return true
  }
  // Check if the group/module is enabled
  for (const [moduleKey, mod] of Object.entries(MODULES)) {
    const allKeys = [...mod.navKeys, ...mod.topNavKeys, ...mod.adminNavKeys]
    if (allKeys.includes(navKey)) {
      if (!mod.alwaysEnabled && enabledModules[moduleKey] === false) return false
      break
    }
  }
  // Check if this specific item is in disabled_items
  if (Array.isArray(disabledItems) && disabledItems.includes(navKey)) return false
  return true
}

/** Keys that require admin role regardless of module. */
export const ROLE_GATED_KEYS = {
  navImport: 'admin',
  navStaleItems: 'admin',
  navLowStock: 'admin',
  navLostDamaged: 'admin',
  navFreelancerTimesheets: 'admin',
  navVenueInquiries: 'admin',
  navPtoInbox: 'admin',
}

/** Keys that require a billing plan feature to be truthy. */
export const PLAN_GATED_KEYS = {
  kioskMode: 'kiosk',
}

/** Keys that require a settings store flag to be truthy. */
export const SETTINGS_GATED_KEYS = {
  navGreenBook: 'gbEnabled',
}

/**
 * Unified nav key availability check — merges role, module, plan, and item gates.
 * Pure function; all context passed as params. Replaces isNavKeyEnabled + inline FEATURE_GATED_KEYS.
 *
 * @param {string} navKey
 * @param {{ adminRole?: boolean, enabledModules?: Object|null, disabledItems?: string[], planFeatures?: Object, settingsFlags?: Object, platformModules?: Object, platformDisabledModules?: Object, policyModules?: Object|null }} ctx
 */
export function isNavKeyAvailable(navKey, ctx = {}) {
  return getNavKeyStatus(navKey, ctx).available
}

/**
 * Status-returning variant of the same checks above. Used by the sidebar to
 * render module/plan-locked items with a lock icon (per Phase 19 audit:
 * "items disappear silently" -> show with explanation).
 *
 * Returns:
 *   { available: true }                    — item is fully accessible
 *   { available: false, reason: 'role',     ... } — role-gated (hide entirely)
 *   { available: false, reason: 'item',     ... } — explicitly disabled item
 *   { available: false, reason: 'platform', moduleKey } — platform kill switch
 *   { available: false, reason: 'module',   moduleKey } — module disabled (upgradeable)
 *   { available: false, reason: 'plan',     planFeatureKey } — plan-gated (upgradeable)
 *   { available: false, reason: 'settings', settingsFlagKey } — settings flag off
 */
export function getNavKeyStatus(navKey, {
  adminRole = false,
  disabledItems = [],
  planFeatures = {},
  settingsFlags = {},
  platformModules = {},
  platformDisabledModules = {},
  policyModules = null,
} = {}) {
  const moduleKey = NAV_KEY_TO_MODULE[navKey]
  const mod = moduleKey ? MODULES[moduleKey] : null

  // When the canonical policy snapshot has loaded (`policyModules`) it is
  // authoritative for module access — it collapses plan entitlement, the
  // platform kill switch and the per-tenant veto into one verdict. Platform
  // denials surface before the role gate; other denials after it. The legacy
  // platform-store fallback below runs only while the snapshot has not loaded.
  // (The tenant module toggle was removed in Phase 6 — there is no longer an
  // enabled_modules fallback.)
  const verdict = (policyModules && moduleKey) ? policyModules[moduleKey] : null

  // Platform-level denial — hard stop, before the role gate.
  if (moduleKey) {
    if (verdict) {
      // alwaysEnabled modules (inventory) are never hidden for module
      // reasons — the resolver only ever denies them via a per-user override.
      if (!verdict.allowed && !mod?.alwaysEnabled
          && (verdict.reason === 'platform_disabled'
              || verdict.reason === 'platform_module_disabled')) {
        return { available: false, reason: 'platform', moduleKey }
      }
    } else {
      // Legacy fallback — platform kill switch + Phase 28 per-tenant lock.
      if (platformModules[moduleKey] === false) {
        return { available: false, reason: 'platform', moduleKey }
      }
      if (platformDisabledModules
          && Object.prototype.hasOwnProperty.call(platformDisabledModules, moduleKey)
          && !mod?.alwaysEnabled) {
        return { available: false, reason: 'platform', moduleKey }
      }
    }
  }

  // Role gate (admin-only) — hide entirely, don't show lock.
  if (ROLE_GATED_KEYS[navKey] === 'admin' && !adminRole) {
    return { available: false, reason: 'role' }
  }

  // Module-level denial — upgrade-to-unlock surface, after the role gate.
  // Only the canonical snapshot decides this; the legacy tenant toggle was
  // removed in Phase 6.
  if (moduleKey && verdict && !verdict.allowed && !mod?.alwaysEnabled) {
    return { available: false, reason: 'module', moduleKey }
  }

  // Plan gate — also upgrade-to-unlock.
  const planFeatureKey = PLAN_GATED_KEYS[navKey]
  if (planFeatureKey && !planFeatures?.[planFeatureKey]) {
    return { available: false, reason: 'plan', planFeatureKey, moduleKey }
  }

  // Settings flag — hide entirely (e.g. greenbook off).
  const settingsFlagKey = SETTINGS_GATED_KEYS[navKey]
  if (settingsFlagKey && !settingsFlags?.[settingsFlagKey]) {
    return { available: false, reason: 'settings', settingsFlagKey }
  }

  // Explicitly disabled item — hide entirely.
  if (Array.isArray(disabledItems) && disabledItems.includes(navKey)) {
    return { available: false, reason: 'item' }
  }

  return { available: true }
}
